import { useLocation, useParams } from "react-router";
import { useCurrentUserQuery } from "../../../hooks/useCurrentUserQuery";
import { useProject } from "../../../hooks/useProject";
import { useRouteSiteId } from "../../../hooks/useRouteSiteId";
import { useSiteCategories } from "../../../hooks/useSiteCategories";
import { useSites } from "../../../hooks/useSites";
import { useVariantByProject } from "../../../hooks/useVariantByProject";
import { MANAGER_URL } from "../../../routes";
import { CurrentUserGroups } from "../../../types/api.types";
import urls from "../../../urls";
import { MENU_ITEMS } from "../Menu.constants";
import { type MenuItemData } from "../Menu.types";
import { AdminItem } from "../MenuItem/AdminItem";
import { MenuItem } from "../MenuItem/MenuItem";
import { OptiChargeItem } from "../MenuItem/OptiChargeItem";
import { useMenuPermissions } from "../useMenuPermissions";

export interface MenuRouteProps {
  menuItemForRoute: MenuItemData;
  isStaff?: boolean;
  adminUrl?: string;
}

export function MenuRoute({
  menuItemForRoute,
  isStaff,
  adminUrl
}: MenuRouteProps) {
  const location = useLocation();
  const isManagerRoute = location.pathname.includes(MANAGER_URL);

  const params = useParams();
  const siteId = useRouteSiteId();
  const projectId = params.projectId;
  const { data: variant } = useVariantByProject(projectId);
  const variantId = variant?.id;

  const { currentUser } = useCurrentUserQuery();
  const { data: sites = [] } = useSites({ projectId });

  const { siteCategories } = useSiteCategories(projectId ?? null, {
    enabled: !!projectId && isManagerRoute
  });

  const { project } = useProject(projectId, {
    enabled: !!projectId,
    refetchInterval: false,
    refetchOnWindowFocus: false
  });

  const { paragraph6Enabled, revenueMonitoringEnabled } = useMenuPermissions({
    variantId: variantId ?? undefined
  });

  const showParagraph6 = paragraph6Enabled;
  const showRevenueMonitoring = !projectId && revenueMonitoringEnabled;
  const showRevenueMonitoringProjectLevel =
    !!projectId && revenueMonitoringEnabled;
  const showVermarktung = showRevenueMonitoring;
  const showVermarktungProjectLevel = showRevenueMonitoringProjectLevel;
  const groupFilteredItems = Object.values<MenuItemData>(MENU_ITEMS).filter(
    (item) =>
      item.url === menuItemForRoute.url ||
      menuItemForRoute.showGroups.includes(item.group)
  );

  const experimentalFilteredItems = groupFilteredItems;

  const projectHasSitesWithInvoicing = sites.some(
    (site) => site.invoicing_active
  );

  const projectHasSitesWithGuaranteeOfOrigin = sites.some(
    (site) => site.go_active
  );

  if (siteCategories) {
    const onlyFullFeedin = siteCategories.every(
      (siteCategory) => siteCategory.is_full_feedin
    );

    const onlyEEPlusAndOrPartialFeedin = siteCategories.every(
      (siteCategory) =>
        siteCategory.is_eigenerzeugung_plus ||
        siteCategory.is_partial_feedin_site
    );

    if (onlyEEPlusAndOrPartialFeedin || onlyFullFeedin) {
      const updatedOnboardingItem = {
        ...MENU_ITEMS.ONBOARDING,
        staff: true
      };

      const onboardingIndex = groupFilteredItems.findIndex(
        (item) => item === MENU_ITEMS.ONBOARDING
      );

      experimentalFilteredItems[onboardingIndex] = updatedOnboardingItem;
    }
  }

  const currentUserFilteredItems = experimentalFilteredItems
    .filter((item) => !item.staff || item.staff === isStaff)
    .filter(
      (item) =>
        item !== MENU_ITEMS.API_SETTINGS || currentUser?.has_api_settings_access
    )
    .filter(
      (item) =>
        item !== MENU_ITEMS.PPA_INVOICING ||
        currentUser?.groups.includes(CurrentUserGroups.PPA)
    );

  //  inject previous menu into apisettings menu
  const itemsWithPersists = currentUserFilteredItems.map((item) => {
    if (item.persistGroups) {
      item.showGroups = menuItemForRoute.showGroups;
    }
    return item;
  });

  let projectFilteredItems = itemsWithPersists.filter(
    (item) =>
      item !== MENU_ITEMS.DELIVERY_OVERVIEW || projectHasSitesWithInvoicing
  );

  projectFilteredItems = projectFilteredItems.filter(
    (item) =>
      item !== MENU_ITEMS.GUARANTEE_OF_ORIGIN ||
      projectHasSitesWithGuaranteeOfOrigin
  );

  projectFilteredItems = projectFilteredItems.filter(
    (item) =>
      item !== MENU_ITEMS.OPTI_CHARGE || (project && project.optichargeActive)
  );

  const personFilteredItems = projectFilteredItems
    .filter((item) => item !== MENU_ITEMS.PARAGRAPH6 || showParagraph6)
    .filter((item) => item !== MENU_ITEMS.VERMARKTUNG || showVermarktung)
    .filter(
      (item) =>
        item !== MENU_ITEMS.VERMARKTUNG_PROJECT_LEVEL ||
        showVermarktungProjectLevel
    )
    .filter(
      (item) => item !== MENU_ITEMS.REVENUE_MONITORING || showRevenueMonitoring
    )
    .filter(
      (item) =>
        item !== MENU_ITEMS.REVENUE_MONITORING_PROJECT_LEVEL ||
        showRevenueMonitoringProjectLevel
    );

  return (
    <>
      {personFilteredItems.map((item) => {
        if (item.label === "Admin") {
          return (
            <AdminItem
              adminUrl={adminUrl}
              key={item.label}
              {...item}
              active={adminUrl === menuItemForRoute.url}
            />
          );
        } else if (item.label === "opti.charge") {
          return (
            <OptiChargeItem
              active={false}
              key={item.label}
              optiChargeUrl={urls.optiCharge()}
              {...item}
            />
          );
        } else {
          return (
            <MenuItem
              key={item.label}
              {...item}
              active={item.url === menuItemForRoute.url}
              projectId={projectId}
              siteId={siteId}
              variantId={variantId}
            />
          );
        }
      })}
    </>
  );
}
